export interface SiteContent {
  id: string;
  type: string;
  content: string;
  created_at: string;
  updated_at: string;
}

let aboutContentCache: {
  content: string;
  timestamp: number;
} | null = null;

const CACHE_DURATION = 5 * 60 * 1000;
const ABOUT_CACHE_KEY = "about-content-cache-v1";

function getFunctionHeaders(): HeadersInit {
  return {
    "Content-Type": "application/json",
    apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
  };
}

export async function getAboutContent(): Promise<string> {
  if (aboutContentCache && Date.now() - aboutContentCache.timestamp < CACHE_DURATION) {
    return aboutContentCache.content;
  }

  try {
    const storedCache = localStorage.getItem(ABOUT_CACHE_KEY);
    if (storedCache) {
      const { content, timestamp } = JSON.parse(storedCache);
      if (Date.now() - timestamp < CACHE_DURATION) {
        aboutContentCache = { content, timestamp };
        return content;
      }
    }
  } catch (error) {
    console.warn("Error reading about content from localStorage:", error);
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/about-content`,
      { method: "GET", headers: getFunctionHeaders(), signal: controller.signal },
    );
    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Failed to fetch about content: ${errorData.error || response.statusText}`);
    }

    const data = await response.json();
    const content = data.content || "No content available";
    aboutContentCache = { content, timestamp: Date.now() };

    try {
      localStorage.setItem(ABOUT_CACHE_KEY, JSON.stringify(aboutContentCache));
    } catch (error) {
      console.warn("Failed to cache about content in localStorage:", error);
    }

    return content;
  } catch (error) {
    console.error("Error fetching about content:", error);
    const staleCache = localStorage.getItem(ABOUT_CACHE_KEY);
    if (staleCache) {
      return JSON.parse(staleCache).content;
    }
    return "Failed to load content. Please try refreshing the page.";
  }
}

export function invalidateAboutCache() {
  aboutContentCache = null;
  try {
    localStorage.removeItem(ABOUT_CACHE_KEY);
  } catch (error) {
    console.warn("Failed to remove about content from localStorage:", error);
  }
}

export async function updateAboutContent(content: string): Promise<void> {
  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/about-content`,
    {
      method: "POST",
      headers: getFunctionHeaders(),
      body: JSON.stringify({ content }),
    },
  );

  const result = await response.json();
  if (!response.ok || !result.success) {
    throw new Error(result.error || "Failed to update about content");
  }

  invalidateAboutCache();
}
